import React, { useState } from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Select,
  MenuItem,
  FormHelperText,
  FormControlLabel,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DeleteOutlineTwoToneIcon from "@mui/icons-material/DeleteOutlineTwoTone";
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import SearchIcon from "@mui/icons-material/Search";
import AddRoleForm from "../components/Permissions/AddRoleForm";
import AddUserForm from "../components/Permissions/AddUserForm";
import UpdateUserForm from "../components/Permissions/UpdateUserForm";
import useFetchWithMsal from "../hooks/useFetchWithMsal";
import { loginRequest, protectedResources } from "../authConfig";
import { useAllBranches } from "../contexts/BranchesContext";
import CustomSnackbar from "../components/CustomSnackbar";
import SalesPromotionsDialog from "../components/SalesPromotionsDialog";
import { usePermissions } from "../components/permissionsHook";

const PermissionsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");

  const {
    updateRole,
    translation,
    tabValue,
    user,
    usersList,
    expanded,
    innerTabValue,
    filteredTabs,
    handleInnerTabChange,
    handleDeleteUser,
    execute,
    rolesList,
    setRolesList,
    snackbar,
    setSnackbar,
    editUserId,
    setEditUserId,
    showAddRoleForm,
    setShowAddRoleForm,
    showAddUserForm,
    setShowAddUserForm,
    mockDropdownData,
    setMockDropdownData,
    allBranches,
    adminPages,
    translateKey,
    handleCheckboxChange,
    handleAccordionChange,
    handleAddUser,
    pageNames,
    handleAddRole,
    handleTabChange,
    disabledCheckbox,
    getDisplayValue,
    handleDeleteRole,
    handleUpdateUser,
  } = usePermissions({
    backendUrl: process.env.REACT_APP_BACKEND_BASE_URL,
  });

  const isAdmin = user?.roles?.some((r) => r.admin === true);

  if (!rolesList.length) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <div className="App">
      <Box display="flex" flexDirection="column" gap={4} p={2}>
        <Typography variant="h6" gutterBottom>
          {translation.systemSettings}
        </Typography>

        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
        >
          <Tab label={translation.permissionsGroups} />
          <Tab label={translation.users} />
          <Tab label={translation.infrastructureData} />
        </Tabs>

        {tabValue === 0 && (
          <Box mt={4}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() =>
                  setSnackbar((prev) => ({ ...prev, open: false, message: "" }))
                }
                autoHideDuration={5000}
              />
              <Typography variant="h6">
                {translation.permissionsGroups}
              </Typography>
              {showAddRoleForm ? (
                <AddRoleForm
                  onAddRole={handleAddRole}
                  onCancel={() => {
                    setShowAddRoleForm(false);
                    setSnackbar({ ...snackbar, open: false });
                  }}
                  pages={adminPages.filter(
                    (p) =>
                      p !== "ViewObligoData" && p !== "ResearchMonitorRole",
                  )}
                />
              ) : (
                <Button
                  onClick={() => {
                    setShowAddRoleForm(true);
                    setSnackbar({ ...snackbar, open: false });
                  }}
                  variant="contained"
                  color="primary"
                >
                  {translation.addRole}
                </Button>
              )}
            </Box>

            <TableContainer component={Paper}>
              <Table aria-label="roles table">
                <TableHead>
                  <TableRow>
                    <TableCell align="center" sx={{}}>
                      {translation.role}
                    </TableCell>
                    <TableCell align="center">{translation.homePage}</TableCell>
                    <TableCell align="center">
                      {translation.tradeApproval}
                    </TableCell>
                    <TableCell align="center">
                      {translation.obligoException}
                    </TableCell>
                    <TableCell align="center">
                      {translation.checkInvoices}
                    </TableCell>
                    <TableCell align="center">
                      {translation.purchaseInvoices}
                    </TableCell>
                    <TableCell align="center">
                      {translation.salesPromotions}
                    </TableCell>
                    <TableCell align="center">
                      {translation.permissions}
                    </TableCell>
                    <TableCell align="center">{translation.timeLine}</TableCell>
                    <TableCell align="center">
                      {translation.eldanPrinter}
                    </TableCell>
                    <TableCell align="center">
                      {translation.chemoRecords}
                    </TableCell>
                    <TableCell align="center">
                      {translation.importManagement}
                    </TableCell>
                    <TableCell align="center">
                      {translation.emailTracking}
                    </TableCell>
                    <TableCell align="center">
                      {translation.researchMonitor}
                    </TableCell>
                    <TableCell align="center">
                      {translation.researchMonitorRole}
                    </TableCell>
                    <TableCell align="center">
                      {translation.fuelControl}
                    </TableCell>
                    <TableCell align="center">
                      {translation.HRCVMatchingPage}
                    </TableCell>
                    <TableCell align="center">
                      {translation.promotionsSystem}
                    </TableCell>
                    <TableCell align="center">
                      {translation.qrCodeDeviceMapping}
                    </TableCell>
                    <TableCell align="center">
                      {translation.tradeDelay}
                    </TableCell>
                    <TableCell align="center">
                      {translation.agentComplaints}
                    </TableCell>
                    <TableCell align="center">{translation.delete}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rolesList && rolesList.length > 0 ? (
                    rolesList
                      .filter((role) =>
                        user?.accessRights?.admin === true
                          ? true
                          : Object.keys(user.accessRights).some(
                              (key) =>
                                user.accessRights[key] === true &&
                                role[key] === true,
                            ),
                      )
                      .map((role) => (
                        <React.Fragment key={role?._id}>
                          <TableRow>
                            <TableCell align="center">{role?.Name}</TableCell>
                            {pageNames.map((page) => {
                              if (page === "ViewObligoData") return null;
                              return (
                                <TableCell key={page} align="center">
                                  {page === "ObligoExceptionPage" ? (
                                    <Select
                                      value={
                                        role?.ObligoExceptionPage
                                          ? "full"
                                          : role?.ViewObligoData
                                            ? "readonly"
                                            : "none"
                                      }
                                      onChange={handleCheckboxChange(
                                        role?.Name,
                                        page,
                                      )}
                                      disabled={disabledCheckbox(page)}
                                      variant="standard"
                                      sx={{ minWidth: 100 }}
                                    >
                                      <MenuItem value="none">
                                        {translation.obligoAccessNone}
                                      </MenuItem>
                                      <MenuItem value="readonly">
                                        {translation.obligoAccessReadonly}
                                      </MenuItem>
                                      <MenuItem value="full">
                                        {translation.obligoAccessFull}
                                      </MenuItem>
                                    </Select>
                                  ) : page === "ResearchMonitorRole" ? (
                                    <Select
                                      value={
                                        role?.ResearchMonitorRole || "none"
                                      }
                                      onChange={handleCheckboxChange(
                                        role?.Name,
                                        page,
                                      )}
                                      disabled={disabledCheckbox(page)}
                                      variant="standard"
                                      sx={{ minWidth: 120 }}
                                    >
                                      <MenuItem value="none">אין גישה</MenuItem>
                                      <MenuItem value="employee">עובד</MenuItem>
                                      <MenuItem value="manager">מנהל</MenuItem>
                                    </Select>
                                  ) : (
                                    <Checkbox
                                      checked={role?.[page]}
                                      onChange={handleCheckboxChange(
                                        role?.Name,
                                        page,
                                      )}
                                      disabled={disabledCheckbox(page)}
                                    />
                                  )}
                                </TableCell>
                              );
                            })}
                            <TableCell align="center">
                              <Tooltip title={translation.deleteRole}>
                                <IconButton
                                  color="warning"
                                  aria-label="delete role"
                                  onClick={() => handleDeleteRole(role?._id)}
                                >
                                  <DeleteOutlineTwoToneIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              colSpan={pageNames.length + 1}
                              style={{ padding: 0 }}
                            >
                              <Accordion
                                expanded={expanded === role?._id}
                                onChange={handleAccordionChange(role?._id)}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                ></AccordionSummary>
                                <AccordionDetails>
                                  <Typography variant="h6" gutterBottom>
                                    סניפים:
                                  </Typography>
                                  <Select
                                    name="Branches"
                                    multiple
                                    value={role?.Branches || []}
                                    onChange={(e) => {
                                      updateRole(e, role);
                                    }}
                                    margin="none"
                                    variant="standard"
                                    fullWidth
                                    required
                                    renderValue={(selected) => {
                                      const selectedBranchesDetails =
                                        selected.map((selectedBranch) => {
                                          return (
                                            allBranches.length &&
                                            allBranches.find(
                                              (branch) =>
                                                branch?.BRANCHNAME ===
                                                selectedBranch.toString(),
                                            )
                                          );
                                        });
                                      if (selectedBranchesDetails.length <= 3) {
                                        return selectedBranchesDetails
                                          .map(
                                            (branch) =>
                                              `${branch?.BRANCHNAME} - ${branch?.BRANCHDES}`,
                                          )
                                          .join(", ");
                                      }

                                      return `${selectedBranchesDetails
                                        .slice(0, 3)
                                        .map(
                                          (branch) =>
                                            `${branch?.BRANCHNAME} - ${branch?.BRANCHDES}`,
                                        )
                                        .join(
                                          ", ",
                                        )} + ${selectedBranchesDetails.length - 3} נוספים`;
                                    }}
                                    style={{
                                      "& .MuiSelectSelect": {
                                        padding: "8px 32px 8px 16px",
                                        fontSize: "16px",
                                        fontWeight: "bold",
                                        whiteSpace: "normal",
                                        height: "auto",
                                        minHeight: "48px",
                                      },
                                    }}
                                  >
                                    {allBranches.length &&
                                      allBranches?.map((branch, index) => (
                                        <MenuItem
                                          key={index}
                                          value={branch.BRANCHNAME}
                                        >
                                          {branch.BRANCHNAME} -{" "}
                                          {branch.BRANCHDES}
                                        </MenuItem>
                                      ))}
                                  </Select>
                                  <FormHelperText>
                                    {translation.selectMultipleBranches}
                                  </FormHelperText>

                                  {role?.SalesPromotionsPage && (
                                    <Box sx={{ mb: 4 }}>
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                          mb: 1,
                                        }}
                                      >
                                        <Typography variant="h6">
                                          {
                                            role?.properties[0]?.SalesPromotions
                                              ?.name
                                          }
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={
                                                  role?.properties[0]
                                                    ?.SalesPromotions
                                                    ?.OrderCreator || false
                                                }
                                                name="OrderCreator"
                                                onChange={(e) => {
                                                  updateRole(e, role);
                                                }}
                                                size="small"
                                              />
                                            }
                                            label={translation.orderCreator}
                                          />
                                          <FormControlLabel
                                            control={
                                              <Checkbox
                                                checked={
                                                  role?.properties[0]
                                                    ?.SalesPromotions
                                                    ?.OrderApprover || false
                                                }
                                                name="OrderApprover"
                                                onChange={(e) => {
                                                  updateRole(e, role);
                                                }}
                                                size="small"
                                              />
                                            }
                                            label={translation.orderApprover}
                                          />
                                        </Box>
                                      </Box>
                                      {role?.properties[0]?.SalesPromotions
                                        ?.OrderCreator && (
                                        <Box
                                          sx={{
                                            p: 2,
                                            border: "1px solid #ddd",
                                            borderRadius: "4px",
                                          }}
                                        >
                                          {role?.properties?.length > 0 ? (
                                            role?.properties?.map(
                                              (property, index) => {
                                                const propertyKey =
                                                  Object.keys(property)[0];
                                                const propertyData =
                                                  property[propertyKey];

                                                return (
                                                  <Box
                                                    key={index}
                                                    sx={{ mb: 2 }}
                                                  >
                                                    {Object.entries(
                                                      propertyData,
                                                    ).map(([key, value], i) => {
                                                      if (
                                                        key === "name" ||
                                                        key ===
                                                          "OrderCreator" ||
                                                        key === "OrderApprover"
                                                      )
                                                        return null;
                                                      return (
                                                        <Typography key={i} component="div" sx={{ mb: 0.5 }}>
                                                          <strong>
                                                            {translateKey(key)}:
                                                          </strong>{" "}
                                                          {key === "family" && Array.isArray(value) ? (
                                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                                              {value.map((f, fi) => {
                                                                const full = mockDropdownData.family?.find(item => item._id === f._id);
                                                                const label = full ? `${full.familyName} - ${full.familyDes}` : f.des;
                                                                return <Chip key={fi} label={label} size="small" />;
                                                              })}
                                                            </Box>
                                                          ) : (
                                                            getDisplayValue(key, value)
                                                          )}
                                                        </Typography>
                                                      );
                                                    })}
                                                  </Box>
                                                );
                                              },
                                            )
                                          ) : (
                                            <Typography>
                                              {translation.noData}
                                            </Typography>
                                          )}
                                          <SalesPromotionsDialog
                                            mockDropdownData={mockDropdownData}
                                            setSnackbar={setSnackbar}
                                            rolesList={rolesList}
                                            setRolesList={setRolesList}
                                            execute={execute}
                                            role={role}
                                          />
                                        </Box>
                                      )}
                                    </Box>
                                  )}
                                </AccordionDetails>
                              </Accordion>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6}>No roles available </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tabValue === 1 && (
          <Box mt={4}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() =>
                  setSnackbar((prev) => ({ ...prev, open: false, message: "" }))
                }
                autoHideDuration={5000}
              />
              <Typography variant="h6">{translation.usersTab}</Typography>
              {showAddUserForm ? (
                <AddUserForm
                  branchesList={allBranches}
                  rolesList={rolesList}
                  onAddUser={handleAddUser}
                  onCancel={() => {
                    setShowAddUserForm(false);
                    setSnackbar({ ...snackbar, open: false });
                  }}
                />
              ) : (
                <Button
                  onClick={() => {
                    setShowAddUserForm(true);
                    setSnackbar({ ...snackbar, open: false });
                  }}
                  variant="contained"
                  color="primary"
                >
                  {translation.addUser}
                </Button>
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                placeholder="חיפוש משתמשים..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 300 }}
              />
            </Box>

            <TableContainer component={Paper}>
              <Table aria-label="users table">
                <TableHead>
                  <TableRow>
                    <TableCell> {translation.name}</TableCell>
                    <TableCell> {translation.email}</TableCell>
                    <TableCell> {translation.username}</TableCell>
                    <TableCell> {translation.roles}</TableCell>
                    <TableCell> {translation.branches}</TableCell>
                    <TableCell>{translation.tradeDelay}</TableCell>
                    {editUserId &&
                      (() => {
                        const editedUser = usersList.find(
                          (u) => u._id === editUserId,
                        );
                        if (editedUser) {
                          const hasChemoRecords = editedUser.Role.some(
                            (roleId) => {
                              const role = rolesList.find(
                                (r) => r._id === roleId,
                              );
                              return role && role.ChemoRecordsPage === true;
                            },
                          );
                          if (hasChemoRecords) {
                            return <TableCell>בית חולים</TableCell>;
                          }
                        }
                        return null;
                      })()}
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {usersList &&
                    usersList
                      .filter(
                        (listUser) =>
                          user.roles.some((role) => role.admin === true) ||
                          listUser.Role.some((roleId) =>
                            user.roles.some(
                              (userRole) => userRole._id === roleId,
                            ),
                          ),
                      )
                      .filter((listUser) => {
                        if (!searchTerm.trim()) return true;
                        const search = searchTerm.toLowerCase();
                        return (
                          listUser.Name?.toLowerCase().includes(search) ||
                          listUser.LastName?.toLowerCase().includes(search) ||
                          listUser.Email?.toLowerCase().includes(search) ||
                          listUser.PriorityUserName?.toLowerCase().includes(
                            search,
                          ) ||
                          `${listUser.Name} ${listUser.LastName}`
                            .toLowerCase()
                            .includes(search)
                        );
                      })
                      .map((user) =>
                        editUserId === user?._id ? (
                          <UpdateUserForm
                            key={user._id}
                            user={user}
                            onUpdateUser={handleUpdateUser}
                            rolesList={rolesList}
                            isAdmin={isAdmin}
                            onCancel={() => {
                              setEditUserId(null);
                              setSnackbar({ ...snackbar, open: false });
                            }}
                          />
                        ) : (
                          <TableRow key={user?._id}>
                            <TableCell>
                              {user?.Name} {user?.LastName}
                            </TableCell>
                            <TableCell>{user?.Email.toLowerCase()}</TableCell>
                            <TableCell>
                              {user?.PriorityUserName?.toLowerCase()}
                            </TableCell>
                            <TableCell>
                              {user?.Role && user?.Role.length > 0
                                ? user?.Role.map((roleId) => {
                                    const role = rolesList.find(
                                      (role) => role?._id === roleId,
                                    );
                                    return role ? role?.Name : "";
                                  }).join(", ")
                                : ""}
                            </TableCell>
                            <TableCell>
                              {user?.Branches && user?.Branches.length > 0 ? (
                                user?.Branches.join(", ")
                              ) : (
                                <span
                                  style={{ fontStyle: "italic", color: "#666" }}
                                >
                                  {translation.allBranchesAccess}
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {user?.tradeDelayRole === "manager" && "מנהל"}
                              {user?.tradeDelayRole === "employee" && "עובד"}
                              {!user?.tradeDelayRole && (
                                <span style={{ color: "#999" }}>—</span>
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={translation.edit}>
                                <IconButton
                                  color="primary"
                                  aria-label="edit user"
                                  onClick={() => {
                                    setEditUserId(user?._id);
                                    setSnackbar({ ...snackbar, open: false });
                                  }}
                                >
                                  <EditTwoToneIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={translation.deleteUser}>
                                <IconButton
                                  color="warning"
                                  aria-label="delete user"
                                  onClick={() => handleDeleteUser(user?._id)}
                                >
                                  <DeleteOutlineTwoToneIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {tabValue === 2 && (
          <Box mt={4}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 3,
              }}
            >
              <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() =>
                  setSnackbar((prev) => ({ ...prev, open: false, message: "" }))
                }
                autoHideDuration={5000}
              />
              <Typography variant="h6">
                {translation.infrastructureData}
              </Typography>
            </Box>

            <Tabs
              value={innerTabValue}
              onChange={handleInnerTabChange}
              indicatorColor="primary"
            >
              {filteredTabs.map((tab, index) => (
                <Tab key={index} label={tab.label} />
              ))}
            </Tabs>

            {filteredTabs[innerTabValue]?.component}
          </Box>
        )}
      </Box>
    </div>
  );
};
export default PermissionsPage;
